import { useState, useEffect } from "react";
import { useFindLocation } from "../../hooks/useFindLocation";
import { useSingleRide } from "../../hooks/ride-operations/useRideQueries";
import Map from "../shared/Map";
import Loading from "../../components/shared/Loading";

function RideModal({ id, type }) {
  // Local state.
  const [departure, setDeparture] = useState(null);
  const [destination, setDestination] = useState(null);
  const [isWarning, setIsWarning] = useState(false);
  const [markerDepartureToggle, setMarkerDepartureToggle] = useState(true);
  const [markerDestinationToggle, setMarkerDestinationToggle] = useState(true);
  const [mapCoordinates, setMapCoordinates] = useState({
    pickUpLat: null,
    pickUpLong: null,
    dropOffLat: null,
    dropOffLong: null,
  });

  // Get the ride data.
  const { rideData, loadingRide, errorRide, refetchRide } = useSingleRide(id);

  // Find rider location using third party API.
  const { findLocation } = useFindLocation();

  // Function for finding locations.
  const loadRideLocations = async (locationType) => {
    try {
      const address =
        locationType === "departure" ? getRide.pickUp : getRide.dropOff;

      const { newCoords, formattedLocationAddress } = await findLocation(
        locationType,
        address
      );

      const isDeparture = locationType === "departure";
      const coordinatesKey = isDeparture ? "pickUp" : "dropOff";

      setMapCoordinates((prevState) => ({
        ...prevState,
        [`${coordinatesKey}Lat`]: newCoords[`${coordinatesKey}Lat`],
        [`${coordinatesKey}Long`]: newCoords[`${coordinatesKey}Long`],
      }));

      if (formattedLocationAddress) {
        isDeparture
          ? setDeparture(formattedLocationAddress)
          : setDestination(formattedLocationAddress);
      } else {
        setIsWarning(true);
      }
    } catch (error) {
      console.error(error);
    }
  };

  // Prefer the coordinates stored on the ride (Places Autocomplete saves
  // lat/lng at creation) — geocoding costs two paid API calls per open.
  // Fall back to geocoding only for rides created without coordinates.
  useEffect(() => {
    const ride = rideData?.getRide;
    if (!ride) return;

    if (ride.pickUpLat != null && ride.dropOffLat != null) {
      setMapCoordinates({
        pickUpLat: ride.pickUpLat,
        pickUpLong: ride.pickUpLng,
        dropOffLat: ride.dropOffLat,
        dropOffLong: ride.dropOffLng,
      });
      setDeparture(ride.pickUp);
      setDestination(ride.dropOff);
      return;
    }

    loadRideLocations("departure");
    loadRideLocations("destination");
  }, [rideData]);

  // Show loading/Error state for queries.
  if (loadingRide) return <Loading />;
  if (errorRide) {
    return <h2>Error, something went wrong</h2>;
  }

  const { getRide } = rideData;

  return (
    <div className="flex flex-col w-full">
      {/* Header */}
      <h3 className="text-white text-xl font-bold text-center mb-4">
        Ride Info
      </h3>

      {/* Warning Banner */}
      {isWarning && (
        <div className="bg-main-red-dark/20 border border-main-red-light/50 rounded-lg p-3 mb-3">
          <p className="text-main-red-light text-sm text-center">
            Addresses in red could not be found on the map. A driver might still
            accept your ride, but consider revising.
          </p>
        </div>
      )}

      {/* Bid Banner */}
      {getRide.bidAmount > 0 && (
        <div className="bg-main-gold-dark/20 border border-main-gold-light/40 rounded-lg p-3 mb-3 text-center">
          <p className="text-main-gold-light text-xs uppercase tracking-wide mb-1">
            Tip
          </p>
          <p className="text-main-gold-light text-2xl font-bold">
            ${getRide.bidAmount.toFixed(2)}
          </p>
        </div>
      )}

      {/* Rider Info Card */}
      <div className="bg-white/5 border border-white/10 rounded-lg p-4 mb-3">
        {/* Profile Picture + Name Row */}
        <div className="flex items-center justify-center gap-3 mb-3 pb-3 border-b border-white/10">
          {getRide.riderProfilePicture ? (
            <img
              src={getRide.riderProfilePicture}
              alt={getRide.riderName}
              className="h-14 w-14 rounded-full object-cover border-2 border-white/30"
            />
          ) : (
            <div className="h-14 w-14 rounded-full bg-main-blue-light/30 flex items-center justify-center">
              <span className="text-white font-medium text-xl">
                {getRide.riderName?.charAt(0)?.toUpperCase() || "R"}
              </span>
            </div>
          )}
          <div>
            <p className="text-white font-medium text-lg">{getRide.riderName}</p>
            <p className="text-main-light-highlight/70 text-sm">{getRide.phoneNumber}</p>
          </div>
        </div>
        <div className="grid grid-cols-2 gap-3 text-center">
          <div>
            <p className="text-main-light-highlight/70 text-xs uppercase tracking-wide">
              Passengers
            </p>
            <p className="text-white font-medium text-sm">{getRide.passengers}</p>
          </div>
          <div>
            <p className="text-main-light-highlight/70 text-xs uppercase tracking-wide">
              Pickup Time
            </p>
            <p className="text-white font-medium text-sm">{getRide.pickUpTime}</p>
          </div>
        </div>
      </div>

      {/* Location Card */}
      <div className="bg-white/5 border border-white/10 rounded-lg p-4 mb-3">
        <div className="flex">
          {/* Timeline column */}
          <div className="flex flex-col items-center mr-3">
            <span className="w-3 h-3 bg-main-green-light rounded-full"></span>
            <div className="w-0.5 bg-white/20 flex-1 my-1"></div>
            <span className="w-3 h-3 bg-main-red-light rounded-full"></span>
          </div>

          {/* Content column */}
          <div className="flex flex-col flex-1 justify-between">
            <div className="pb-3">
              <p className="text-main-green-light text-xs uppercase tracking-wide">
                Pick Up
              </p>
              <p
                className={`font-medium text-sm text-center ${
                  departure ? "text-white" : "text-main-red-light"
                }`}
              >
                {departure || getRide.pickUp}
              </p>
            </div>
            <div className="pt-3">
              <p className="text-main-red-light text-xs uppercase tracking-wide">
                Drop Off
              </p>
              <p
                className={`font-medium text-sm text-center ${
                  destination ? "text-white" : "text-main-red-light"
                }`}
              >
                {destination || getRide.dropOff}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Map */}
      <div className="w-full h-40 md:h-52 rounded-lg overflow-hidden border border-white/10 mb-3">
        <Map
          coordinatesData={mapCoordinates}
          markerDepartureToggle={markerDepartureToggle}
          markerDestinationToggle={markerDestinationToggle}
          readOnly
        />
      </div>
    </div>
  );
}

export default RideModal;
